import React from 'react';
import { ALL_GAMES } from '../../config/games';
import { renderAvatar } from '../../utils/avatarUtils';

interface GameStatisticsData {
  gameId: string;
  roomName: string;
  gameType: string;
  playedAt: number;
  leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
  statistics: {
    mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
    wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
    totalQuestions: number;
    averageScore: number;
    hotTakesStats?: {
      totalQuestions: number;
      totalTies: number;
      mostDrinks: { playerId: string; playerName: string; count: number } | null;
      mostControversial: { statement: string; agreeCount: number; disagreeCount: number } | null;
      playerDrinks: Array<{ playerId: string; playerName: string; count: number }>;
    };
    hotTakesResults?: Array<{
      questionId: string;
      statement: string;
      agreeCount: number;
      disagreeCount: number;
      minorityVote: 'AGREE' | 'DISAGREE' | 'tie';
      debatePrompt?: string;
      votes: Array<{
        playerId: string;
        playerName: string;
        avatar?: string;
        vote: 'AGREE' | 'DISAGREE';
        isMinority: boolean;
        mustDrink: boolean;
      }>;
    }>;
  };
  players: Array<{ id: string; name: string; avatar?: string }>;
}

interface GameStatsCardProps {
  game: GameStatisticsData;
  getPlayerAvatar?: (playerId: string) => string | undefined;
}

export const GameStatsCard: React.FC<GameStatsCardProps> = ({ game, getPlayerAvatar }) => {
  const gameConfig = ALL_GAMES.find(g => g.id === game.gameType);
  const gameName = gameConfig?.name || game.gameType;
  const gameIcon = gameConfig?.icon || '🎮';
  const playedDate = new Date(game.playedAt);
  
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const medals = ['🥇', '🥈', '🥉'];

  const statBoxStyle: React.CSSProperties = {
    background: 'var(--bg-secondary)',
    padding: '0.75rem',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-light)',
  };

  return (
    <div
      style={{
        background: 'var(--surface-muted)',
        borderRadius: 'var(--radius-md)',
        padding: '1.5rem',
        border: '1px solid var(--border-accent)',
        marginBottom: '1rem',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '1rem', borderBottom: '1px solid var(--border-light)', paddingBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.5rem' }}>{gameIcon}</span>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.2rem', margin: 0 }}>{gameName}</h3>
          </div>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            {formatDate(playedDate)}
          </span>
        </div>
      </div>

      {/* Leaderboard */}
      <div style={{ marginBottom: '1rem' }}>
        <h4 style={{ color: 'var(--color-primary)', fontSize: '1rem', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' }}>
          Final Leaderboard
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {game.leaderboard.slice(0, 5).map((entry, index) => {
            const player = game.players.find(p => p.id === entry.playerId);
            const avatar = getPlayerAvatar ? getPlayerAvatar(entry.playerId) : player?.avatar;
            return (
              <div
                key={entry.playerId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.5rem',
                  background: index < 3 ? 'rgba(var(--color-primary-rgb), 0.08)' : 'var(--bg-secondary)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <span style={{ fontSize: '1.2rem', width: '1.5rem' }}>
                  {index < 3 ? medals[index] : `${index + 1}.`}
                </span>
                {renderAvatar(avatar, '1.5rem')}
                <span style={{ flex: 1, color: 'var(--text-primary)', fontSize: '0.95rem' }}>{entry.playerName}</span>
                <span style={{ color: 'var(--color-primary)', fontWeight: 'bold', fontSize: '0.95rem' }}>
                  {entry.score} pts
                </span>
              </div>
            );
          })}
          {game.leaderboard.length > 5 && (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', marginTop: '0.25rem' }}>
              +{game.leaderboard.length - 5} more players
            </div>
          )}
        </div>
      </div>

      {/* Statistics Summary */}
      <div>
        <h4 style={{ color: 'var(--color-primary)', fontSize: '1rem', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' }}>
          Statistics
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
          {game.statistics.totalQuestions !== undefined && (
            <div style={statBoxStyle}>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                Questions
              </div>
              <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                {game.statistics.totalQuestions}
              </div>
            </div>
          )}
          {game.statistics.averageScore !== undefined && (
            <div style={statBoxStyle}>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                Avg Score
              </div>
              <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                {game.statistics.averageScore}
              </div>
            </div>
          )}
          {game.statistics.mostCorrectAnswers && game.statistics.mostCorrectAnswers.count > 0 && (
            <div style={statBoxStyle}>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                Most Correct
              </div>
              <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem', fontWeight: 'bold' }}>
                {game.statistics.mostCorrectAnswers.playerName}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                {game.statistics.mostCorrectAnswers.count}
              </div>
            </div>
          )}
          {game.statistics.hotTakesStats && (
            <>
              {game.statistics.hotTakesStats.mostDrinks && (
                <div style={statBoxStyle}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    Most Drinks
                  </div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem', fontWeight: 'bold' }}>
                    {game.statistics.hotTakesStats.mostDrinks.playerName}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                    {game.statistics.hotTakesStats.mostDrinks.count} drinks
                  </div>
                </div>
              )}
              {game.statistics.hotTakesStats.totalTies > 0 && (
                <div style={statBoxStyle}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    Total Ties
                  </div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                    {game.statistics.hotTakesStats.totalTies}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameStatsCard;
